/** @file useStaleBuild: polls /api/v1/health every 60 s and on focus; stale when build.sha differs. */
import { useEffect, useState } from 'react';
import { buildInfo } from './buildInfo';
import { serverInfo } from './serverInfo';

/** What the hook watches: the server `url` (null = none), the editor's own sha, the transport and the poll period. */
export type StaleBuildOptions = { sha?: string; fetchFn?: typeof fetch; intervalMs?: number };

/** `stale`: a newer deploy is live; `version`: the server's version once it answered (null before). */
export type StaleBuild = { stale: boolean; version: string | null };

/** An editor with no injected build (`dev`) has nothing to compare with, so it never calls itself stale. */
const comparable = (sha: string): boolean => sha !== 'dev' && sha !== 'unknown';

/**
 * Asks the server for its build every `intervalMs` (default 60 s) and whenever the window gains focus. A failed
 * poll changes nothing; the answer is never acted on (the user reloads), only reported.
 */
export function useStaleBuild(url: string | null, options: StaleBuildOptions = {}): StaleBuild {
  const [state, setState] = useState<StaleBuild>({ stale: false, version: null });
  const { sha = buildInfo().sha, fetchFn = fetch, intervalMs = 60_000 } = options;
  useEffect(() => {
    if (url === null) return undefined;
    let live = true;
    const poll = (): void => {
      serverInfo(url, fetchFn).then((health) => {
        if (!live) return;
        const server = health.build?.sha;
        setState({ stale: comparable(sha) && server !== undefined && server !== sha, version: health.version });
      }, () => undefined);
    };
    poll();
    const timer = setInterval(poll, intervalMs);
    globalThis.addEventListener('focus', poll);
    return () => {
      live = false;
      clearInterval(timer);
      globalThis.removeEventListener('focus', poll);
    };
  }, [url, sha, fetchFn, intervalMs]);
  return state;
}
